// ** MUI Imports
import { lighten, darken } from '@mui/material/styles'

// ** Util Import
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'

/*
  El texto de un <Alert> salia de darken(main, 0.12) sobre un fondo que es ese
  mismo main al 12%. O sea: el texto era el color de la alerta apenas un 12% mas
  oscuro, encima de una version clarita del mismo color. Medido: 2,15:1 en
  warning, 2,76:1 en info, 2,08:1 en success. WCAG AA pide 4,5:1 y esas alertas
  llevan instrucciones que el usuario necesita leer ("guarda antes de salir de
  la pagina").

  0,5 en claro deja los cuatro tonos entre 5,0 y 10,6:1. En oscuro el ajuste es
  al reves —aclarar— y hace falta mas: con 0,12 el rojo se quedaba en 3,16:1;
  con 0,4 sube a 4,94:1 y los otros tres pasan de 6:1.

  Solo cambia el texto y el icono. El fondo sigue siendo main al 12%, asi que
  las alertas se ven igual de suaves que antes.
*/
const Alert = mode => {
  const esOscuro = mode === 'dark'
  const intensidad = esOscuro ? 0.4 : 0.5
  const getColor = (color, _ignorado) => (esOscuro ? lighten(color, intensidad) : darken(color, intensidad))

  return {
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 5,
          '& .MuiAlertTitle-root': {
            marginBottom: theme.spacing(1.6)
          },
          '& a': {
            fontWeight: 500,
            color: 'inherit'
          }
        }),
        standardSuccess: ({ theme }) => ({
          color: getColor(theme.palette.success.main, 0.12),
          backgroundColor: hexToRGBA(theme.palette.success.main, 0.12),
          '& .MuiAlertTitle-root': {
            color: getColor(theme.palette.success.main, 0.12)
          },
          '& .MuiAlert-icon': {
            color: getColor(theme.palette.success.main, 0.12)
          }
        }),
        standardInfo: ({ theme }) => ({
          color: getColor(theme.palette.info.main, 0.12),
          backgroundColor: hexToRGBA(theme.palette.info.main, 0.12),
          '& .MuiAlertTitle-root': {
            color: getColor(theme.palette.info.main, 0.12)
          },
          '& .MuiAlert-icon': {
            color: getColor(theme.palette.info.main, 0.12)
          }
        }),
        standardWarning: ({ theme }) => ({
          color: getColor(theme.palette.warning.main, 0.12),
          backgroundColor: hexToRGBA(theme.palette.warning.main, 0.12),
          '& .MuiAlertTitle-root': {
            color: getColor(theme.palette.warning.main, 0.12)
          },
          '& .MuiAlert-icon': {
            color: getColor(theme.palette.warning.main, 0.12)
          }
        }),
        standardError: ({ theme }) => ({
          color: getColor(theme.palette.error.main, 0.12),
          backgroundColor: hexToRGBA(theme.palette.error.main, 0.12),
          '& .MuiAlertTitle-root': {
            color: getColor(theme.palette.error.main, 0.12)
          },
          '& .MuiAlert-icon': {
            color: getColor(theme.palette.error.main, 0.12)
          }
        }),
        outlinedSuccess: ({ theme }) => ({
          borderColor: theme.palette.success.main,
          color: getColor(theme.palette.success.main, 0.12),
          '& .MuiAlertTitle-root': {
            color: getColor(theme.palette.success.main, 0.12)
          },
          '& .MuiAlert-icon': {
            color: getColor(theme.palette.success.main, 0.12)
          }
        }),
        outlinedInfo: ({ theme }) => ({
          borderColor: theme.palette.info.main,
          color: getColor(theme.palette.info.main, 0.12),
          '& .MuiAlertTitle-root': {
            color: getColor(theme.palette.info.main, 0.12)
          },
          '& .MuiAlert-icon': {
            color: getColor(theme.palette.info.main, 0.12)
          }
        }),
        outlinedWarning: ({ theme }) => ({
          borderColor: theme.palette.warning.main,
          color: getColor(theme.palette.warning.main, 0.12),
          '& .MuiAlertTitle-root': {
            color: getColor(theme.palette.warning.main, 0.12)
          },
          '& .MuiAlert-icon': {
            color: getColor(theme.palette.warning.main, 0.12)
          }
        }),
        outlinedError: ({ theme }) => ({
          borderColor: theme.palette.error.main,
          color: getColor(theme.palette.error.main, 0.12),
          '& .MuiAlertTitle-root': {
            color: getColor(theme.palette.error.main, 0.12)
          },
          '& .MuiAlert-icon': {
            color: getColor(theme.palette.error.main, 0.12)
          }
        }),
        filled: ({ theme, ownerState }) => ({
          fontWeight: 400,
          /*
            Antes forzaba blanco. En una alerta rellena el fondo es el `main` del
            color, y warning, info y success son claros: el blanco daba 1,78, 2,40
            y 1,71:1. contrastText ya trae el tono correcto para cada uno.
          */
          color: theme.palette[ownerState?.color ?? ownerState?.severity ?? 'info']?.contrastText ?? theme.palette.common.white
        })
      }
    }
  }
}

export default Alert
